import React, { useState } from "react";
import { Plus, Cog, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useList, useDeleteOne, useBulkDelete } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState, StatusBadge, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const M_STATUS = ["Running", "Idle", "Setup", "Maintenance", "Breakdown", "Offline"];

export default function Machines() {
  const { canWrite, user } = useAuth();
  const qc = useQueryClient();
  const delOne = useDeleteOne((id) => `/machines/${id}`, ["machines"]);
  const clearAll = useBulkDelete((m) => `/machines/${m.id}`, ["machines"]);
  const { data: machines, isLoading } = useList("machines", "/machines");
  const { data: departments = [] } = useList("departments", "/departments");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", machine_type: "", department: "", capacity_per_hour: "", status: "Idle" });

  const setStatus = async (m, status) => {
    try { await api.put(`/machines/${m.id}`, { status }); qc.invalidateQueries(); toast.success(`${m.name} → ${status}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const create = async () => {
    if (!f.name) return toast.error("Name required");
    try { await api.post("/machines", { ...f, capacity_per_hour: Number(f.capacity_per_hour) || 0 }); toast.success("Machine added"); qc.invalidateQueries(); setOpen(false); setF({ name: "", machine_type: "", department: "", capacity_per_hour: "", status: "Idle" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (isLoading) return <Loading />;
  return (
    <div>
      <PageHeader title="Machine Management" subtitle="Master list & live status" testid="machines-header">
        {canWrite("machines") && <Button onClick={() => setOpen(true)} className="gap-2" data-testid="new-machine-btn"><Plus className="w-4 h-4" /> Add Machine</Button>}
      </PageHeader>
      {!machines?.length ? <EmptyState title="No machines" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {machines.map((m) => (
            <Card key={m.id} className="p-4 kpi-card" data-testid={`machine-card-${m.machine_code}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-primary/12 text-primary flex items-center justify-center"><Cog className="w-5 h-5" /></div>
                  <div><div className="font-semibold text-sm">{m.name}</div><div className="text-xs text-muted-foreground font-num">{m.machine_code}</div></div>
                </div>
                <StatusBadge status={m.status} />
                {canWrite("machines") && <Button size="icon" variant="ghost" className="h-7 w-7 -mt-1 -mr-1" onClick={() => delOne(m.id, `Delete machine ${m.name}?`)} data-testid={`delete-machine-${m.machine_code}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
              </div>
              <div className="mt-3 text-xs text-muted-foreground space-y-0.5">
                <div>Type: <span className="text-foreground">{m.machine_type || "—"}</span></div>
                <div>Dept: <span className="text-foreground">{m.department || "—"}</span></div>
                <div>Capacity: <span className="text-foreground font-num">{fmt(m.capacity_per_hour)}/hr</span></div>
                <div>Next Maint: <span className="text-foreground">{m.next_maintenance || "—"}</span></div>
              </div>
              {canWrite("machines") && (
                <Select value={m.status} onValueChange={(v) => setStatus(m, v)}>
                  <SelectTrigger className="mt-3 h-9" data-testid={`machine-status-${m.machine_code}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{M_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </Card>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="machine-form">
          <DialogHeader><DialogTitle>Add Machine</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="machine-name-input" /></div>
            <div><Label>Type</Label><Input value={f.machine_type} onChange={(e) => setF({ ...f, machine_type: e.target.value })} /></div>
            <div><Label>Department</Label>
              <Select value={f.department} onValueChange={(v) => setF({ ...f, department: v })}>
                <SelectTrigger><SelectValue placeholder="Dept" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Capacity/hr</Label><Input type="number" value={f.capacity_per_hour} onChange={(e) => setF({ ...f, capacity_per_hour: e.target.value })} /></div>
            <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{M_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="machine-save-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
